/**
 * AdminApplicationReview - Admin Dashboard for Reviewing Applications
 * Review, approve, or reject PPV, Business, and PUDO applications
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Truck,
  Store,
  MapPin,
  Calendar,
  User,
  Mail,
  Phone,
  FileText,
  Eye,
  RefreshCw,
  Loader2,
  Send,
  ChevronDown,
  MoreVertical,
  Download,
  Trash2,
  UserPlus,
  Plus
} from 'lucide-react';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-blue-500', textColor: 'text-blue-600' },
  submitted: { label: 'Submitted', color: 'bg-blue-500', textColor: 'text-blue-600' },
  under_review: { label: 'Under Review', color: 'bg-amber-500', textColor: 'text-amber-600' },
  approved: { label: 'Approved', color: 'bg-green-500', textColor: 'text-green-600' },
  rejected: { label: 'Rejected', color: 'bg-red-500', textColor: 'text-red-600' }
};

const TYPE_CONFIG = {
  ppv: { label: 'PPV Driver', icon: Truck, color: 'text-emerald-500', bgColor: 'bg-emerald-100' },
  driver: { label: 'PPV Driver', icon: Truck, color: 'text-emerald-500', bgColor: 'bg-emerald-100' },
  business: { label: 'Business', icon: Store, color: 'text-blue-500', bgColor: 'bg-blue-100' },
  pudo: { label: 'PUDO Centre', icon: MapPin, color: 'text-purple-500', bgColor: 'bg-purple-100' },
  seller: { label: "Seller", icon: Store, color: "text-emerald-500", bgColor: "bg-emerald-100" },
  vendor: { label: "Seller", icon: Store, color: "text-emerald-500", bgColor: "bg-emerald-100" },
  role_request: { label: "Role Request", icon: Store, color: "text-cyan-500", bgColor: "bg-cyan-100" },
};

export default function AdminApplicationReview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [nearbyPudos, setNearbyPudos] = useState([]);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const [processing, setProcessing] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('pending');
  
  // Action form
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  
  // Quick Add Modal
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [quickAddType, setQuickAddType] = useState('driver'); // driver, seller, pudo
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [quickAddForm, setQuickAddForm] = useState({
    email: '',
    name: '',
    phone: '',
    business_name: '',
    trn: '',
    pudo_address: '',
    pudo_city: '',
    pudo_parish: 'Kingston',
    pudo_hours: 'Mon-Sat 9:00 AM - 6:00 PM',
    vehicle_type: 'car',
    license_plate: '',
    send_credentials_email: true
  });

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    filterApplications();
  }, [applications, searchQuery, statusFilter, typeFilter, activeTab]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      // Fetch all application types
      const [ppvRes, businessRes, pudoRes, roleReqRes] = await Promise.all([
        api.get('/admin/ppv-applications').catch(() => ({ data: [] })),
        api.get('/admin/business-applications').catch(() => ({ data: [] })),
        api.get('/admin/pudo-applications').catch(() => ({ data: [] })),
        api.get('/role-requests/admin/pending').catch(() => ({ data: { requests: [] } }))
      ]);
      
      const allApps = [
        ...(ppvRes.data || []).map(a => ({ ...a, type: 'ppv' })),
        ...(businessRes.data || []).map(a => ({ ...a, type: a.type || 'business' })),
        ...(pudoRes.data || []).map(a => ({ ...a, type: 'pudo' })),
        ...(roleReqRes.data?.requests || []).map(a => ({ ...a, type: a.request_type === 'seller' ? 'seller' : a.request_type === 'pudo' ? 'pudo' : 'role_request', source: 'role_request', is_role_request: true }))
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setApplications(allApps);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = [...applications];
    
    // Tab filter (status groups)
    if (activeTab === 'pending') {
      filtered = filtered.filter(a => ['pending', 'submitted', 'under_review'].includes(a.status?.toLowerCase()));
    } else if (activeTab === 'approved') {
      filtered = filtered.filter(a => a.status?.toLowerCase() === 'approved');
    } else if (activeTab === 'rejected') {
      filtered = filtered.filter(a => a.status?.toLowerCase() === 'rejected');
    }
    
    // Type filter
    if (typeFilter !== 'all') {
      if (typeFilter === 'seller') {
        filtered = filtered.filter(a => a.type === 'seller' || a.type === 'vendor' || (a.type === 'business' && a.source === 'vendor_applications'));
      } else {
        filtered = filtered.filter(a => a.type === typeFilter);
      }
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        a.business_name?.toLowerCase().includes(query) ||
        a.full_name?.toLowerCase().includes(query) ||
        a.name?.toLowerCase().includes(query) ||
        a.email?.toLowerCase().includes(query) ||
        a.phone?.includes(query)
      );
    }
    
    setFilteredApps(filtered);
  };

  const handleViewDetails = async (app) => {
    setSelectedApp(app);
    setShowDetailDialog(true);
    setNearbyPudos([]);
    
    // Fetch nearby PUDOs if coordinates exist and its a PUDO application
    if ((app.type === "pudo" || app.type === "seller") && app.latitude && app.longitude) {
      setLoadingNearby(true);
      try {
        const response = await api.get("/admin/pudo-locations/nearby", {
          params: { lat: app.latitude, lng: app.longitude, radius_km: 5 }
        });
        setNearbyPudos(response.data.nearby_pudos || []);
      } catch (error) {
        console.error("Failed to fetch nearby PUDOs:", error);
      } finally {
        setLoadingNearby(false);
      }
    }
  };

  const handleAction = (app, action) => {
    setSelectedApp(app);
    setActionType(action);
    setRejectionReason('');
    setAdminNotes('');
    setSendEmail(true);
    setShowActionDialog(true);
  };

  const submitAction = async () => {
    if (!selectedApp) return;
    
    if (actionType === 'reject' && !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setProcessing(true);
    try {
      let endpoint;
      if (selectedApp.is_role_request || selectedApp.source === 'role_request') {
        endpoint = `/role-requests/admin/${selectedApp.id}/${actionType}`;
      } else {
        const apiType = selectedApp.type === 'seller' || selectedApp.type === 'vendor' ? 'business' : selectedApp.type;
        endpoint = `/admin/${apiType}-applications/${selectedApp.id}/${actionType}`;
      }
      await api.post(endpoint, {
        rejection_reason: rejectionReason || null,
        admin_notes: adminNotes || null,
        send_email: sendEmail
      });
      
      toast.success(`Application ${actionType === 'approve' ? 'approved' : 'rejected'} successfully`);
      setShowActionDialog(false);
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${actionType} application`);
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (app) => {
    if (!confirm(`Delete application for ${app.business_name || app.full_name || app.name}? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/admin/applications/${app.id}`);
      toast.success("Application deleted");
      fetchApplications();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete application");
    }
  };

  // Quick Add Handler
  const handleQuickAdd = async () => {
    if (!quickAddForm.email || !quickAddForm.name) {
      toast.error('Email and name are required');
      return;
    }
    
    setQuickAddLoading(true);
    try {
      let endpoint = '';
      let payload = {};
      
      if (quickAddType === 'driver') {
        endpoint = '/admin/drivers/quick-add';
        payload = {
          email: quickAddForm.email,
          name: quickAddForm.name,
          phone: quickAddForm.phone,
          vehicle_type: quickAddForm.vehicle_type,
          license_plate: quickAddForm.license_plate,
          send_credentials_email: quickAddForm.send_credentials_email
        };
      } else {
        endpoint = '/admin/businesses/create';
        payload = {
          owner_email: quickAddForm.email,
          owner_name: quickAddForm.name,
          owner_phone: quickAddForm.phone,
          business_name: quickAddForm.business_name || quickAddForm.name + "'s Business",
          business_type: 'retail',
          trn: quickAddForm.trn,
          is_pudo: quickAddType === 'pudo',
          send_credentials_email: quickAddForm.send_credentials_email,
          pudo_address: quickAddForm.pudo_address,
          pudo_city: quickAddForm.pudo_city,
          pudo_parish: quickAddForm.pudo_parish,
          pudo_hours: quickAddForm.pudo_hours
        };
      }
      
      await api.post(endpoint, payload);
      toast.success(`${quickAddType.charAt(0).toUpperCase() + quickAddType.slice(1)} added successfully!`);
      setShowQuickAddModal(false);
      setQuickAddForm({
        email: '',
        name: '',
        phone: '',
        business_name: '',
        trn: '',
        pudo_address: '',
        pudo_city: '',
        pudo_parish: 'Kingston',
        pudo_hours: 'Mon-Sat 9:00 AM - 6:00 PM',
        vehicle_type: 'car',
        license_plate: '',
        send_credentials_email: true
      });
      fetchApplications();
    } catch (error) {
      console.error('Quick add error:', error);
      toast.error(error.response?.data?.detail || 'Failed to add partner');
    } finally {
      setQuickAddLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-JM', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypeConfig = (type) => TYPE_CONFIG[type] || TYPE_CONFIG.business;
  const getStatusConfig = (status) => STATUS_CONFIG[status?.toLowerCase()] || STATUS_CONFIG.pending;

  // Stats
  const stats = {
    total: applications.length,
    pending: applications.filter(a => ['pending', 'submitted', 'under_review'].includes(a.status?.toLowerCase())).length,
    approved: applications.filter(a => a.status?.toLowerCase() === 'approved').length,
    rejected: applications.filter(a => a.status?.toLowerCase() === 'rejected').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#00A86B] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} className="text-slate-400 hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#00A86B]" />
                  Application Review
                </h1>
                <p className="text-sm text-slate-400">Review and manage partner applications</p>
              </div>
            </div>
            <Button onClick={fetchApplications} variant="outline" size="sm" className="border-slate-700">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-slate-800/50 border-slate-700 p-4 text-center">
            <p className="text-3xl font-bold text-white">{stats.total}</p>
            <p className="text-sm text-slate-400">Total</p>
          </Card>
          <Card className="bg-amber-500/10 border-amber-500/30 p-4 text-center">
            <p className="text-3xl font-bold text-amber-400">{stats.pending}</p>
            <p className="text-sm text-amber-400/70">Pending Review</p>
          </Card>
          <Card className="bg-green-500/10 border-green-500/30 p-4 text-center">
            <p className="text-3xl font-bold text-green-400">{stats.approved}</p>
            <p className="text-sm text-green-400/70">Approved</p>
          </Card>
          <Card className="bg-red-500/10 border-red-500/30 p-4 text-center">
            <p className="text-3xl font-bold text-red-400">{stats.rejected}</p>
            <p className="text-sm text-red-400/70">Rejected</p>
          </Card>
        </div>

        {/* Quick Add Section */}
        <Card className="bg-gradient-to-r from-slate-800/50 to-slate-900/50 border-slate-700 p-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#00A86B]/20 rounded-lg">
                <UserPlus className="w-5 h-5 text-[#00A86B]" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Quick Add Partners</h3>
                <p className="text-sm text-slate-400">Instantly approve drivers, sellers, or PUDO locations</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => { setQuickAddType('driver'); setShowQuickAddModal(true); }}
                className="bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="quick-add-driver-btn"
              >
                <Truck className="w-4 h-4 mr-2" />
                Add Driver
              </Button>
              <Button
                onClick={() => { setQuickAddType('seller'); setShowQuickAddModal(true); }}
                className="bg-blue-600 hover:bg-blue-500 text-white"
                data-testid="quick-add-seller-btn"
              >
                <Store className="w-4 h-4 mr-2" />
                Add Seller
              </Button>
              <Button
                onClick={() => { setQuickAddType('pudo'); setShowQuickAddModal(true); }}
                className="bg-purple-600 hover:bg-purple-500 text-white"
                data-testid="quick-add-pudo-btn"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Add PUDO
              </Button>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-48 bg-slate-800 border-slate-700">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="ppv">PPV Drivers</SelectItem>
              <SelectItem value="business">Businesses</SelectItem>
              <SelectItem value="pudo">PUDO Centres</SelectItem>
              <SelectItem value="seller">Sellers</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="pending" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
              <Clock className="w-4 h-4 mr-2" />
              Pending ({stats.pending})
            </TabsTrigger>
            <TabsTrigger value="approved" className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400">
              <CheckCircle className="w-4 h-4 mr-2" />
              Approved ({stats.approved})
            </TabsTrigger>
            <TabsTrigger value="rejected" className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400">
              <XCircle className="w-4 h-4 mr-2" />
              Rejected ({stats.rejected})
            </TabsTrigger>
            <TabsTrigger value="all" className="data-[state=active]:bg-slate-700">
              All ({stats.total})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {filteredApps.length === 0 ? (
              <Card className="p-12 bg-slate-800/50 border-slate-700 text-center">
                <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No applications found</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredApps.map((app) => {
                  const typeConfig = getTypeConfig(app.type);
                  const statusConfig = getStatusConfig(app.status);
                  const TypeIcon = typeConfig.icon;
                  
                  return (
                    <Card key={`${app.type}-${app.id}`} className="bg-slate-800/50 border-slate-700 overflow-hidden">
                      <CardContent className="p-0">
                        <div className="flex items-stretch">
                          <div className={`w-1 ${statusConfig.color}`} />
                          <div className="flex-1 p-5">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start gap-4">
                                <div className={`w-12 h-12 rounded-xl ${typeConfig.bgColor} flex items-center justify-center`}>
                                  <TypeIcon className={`w-6 h-6 ${typeConfig.color}`} />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <h3 className="font-semibold text-white">
                                      {app.business_name || app.full_name || app.name || 'Unnamed'}
                                    </h3>
                                    <Badge className={`${statusConfig.color} text-white text-xs`}>
                                      {statusConfig.label}
                                    </Badge>
                                    <Badge variant="outline" className="text-xs border-slate-600">
                                      {typeConfig.label}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-4 text-sm text-slate-400">
                                    {app.email && (
                                      <span className="flex items-center gap-1">
                                        <Mail className="w-3 h-3" />
                                        {app.email}
                                      </span>
                                    )}
                                    {app.phone && (
                                      <span className="flex items-center gap-1">
                                        <Phone className="w-3 h-3" />
                                        {app.phone}
                                      </span>
                                    )}
                                    <span className="flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {formatDate(app.created_at)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewDetails(app)}
                                  className="text-slate-400 hover:text-white"
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                                
                                {['pending', 'submitted', 'under_review'].includes(app.status?.toLowerCase()) && (
                                  <>
                                    <Button
                                      size="sm"
                                      onClick={() => handleAction(app, 'approve')}
                                      className="bg-green-600 hover:bg-green-700"
                                    >
                                      <CheckCircle className="w-4 h-4 mr-1" />
                                      Approve
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => handleAction(app, 'reject')}
                                    >
                                      <XCircle className="w-4 h-4 mr-1" />
                                      Reject
                                    </Button>
                                  </>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 hover:bg-red-500/10"
                                  onClick={(e) => { e.stopPropagation(); handleDelete(app); }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#00A86B]" />
              Application Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedApp && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-400">Type</label>
                  <p className="font-medium">{getTypeConfig(selectedApp.type).label}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-400">Status</label>
                  <Badge className={`${getStatusConfig(selectedApp.status).color} text-white`}>
                    {getStatusConfig(selectedApp.status).label}
                  </Badge>
                </div>
                <div>
                  <label className="text-xs text-slate-400">Name</label>
                  <p className="font-medium">{selectedApp.business_name || selectedApp.full_name || selectedApp.name}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-400">Email</label>
                  <p className="font-medium">{selectedApp.email}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-400">Phone</label>
                  <p className="font-medium">{selectedApp.phone}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-400">Submitted</label>
                  <p className="font-medium">{formatDate(selectedApp.created_at)}</p>
                </div>
              </div>
              
              {selectedApp.address && (
                <div>
                  <label className="text-xs text-slate-400">Address</label>
                  <p className="font-medium">{selectedApp.address}</p>
                </div>
              )}
              
              {selectedApp.parish && (
                <div>
                  <label className="text-xs text-slate-400">Parish</label>
                  <p className="font-medium">{selectedApp.parish}</p>
                </div>
              )}
              
              {selectedApp.vehicle_type && (
                <div>
                  <label className="text-xs text-slate-400">Vehicle Type</label>
                  <p className="font-medium">{selectedApp.vehicle_type}</p>
                </div>
              )}
              
              {selectedApp.business_type && (
                <div>
                  <label className="text-xs text-slate-400">Business Type</label>
                  <p className="font-medium">{selectedApp.business_type}</p>
                </div>
              )}
              
              {selectedApp.description && (
                <div>
                  <label className="text-xs text-slate-400">Description</label>
                  <p className="text-slate-300">{selectedApp.description}</p>
                </div>
              )}
              
              {/* Driver/PPV Specific Details */}
              {selectedApp.type === 'ppv' && selectedApp.basic_info && Object.keys(selectedApp.basic_info).length > 0 && (
                <div className="border-t border-slate-700 pt-4 mt-4">
                  <h4 className="text-emerald-400 font-semibold mb-3">Driver Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedApp.basic_info.full_name && (
                      <div>
                        <label className="text-xs text-slate-400">Full Name</label>
                        <p className="font-medium">{selectedApp.basic_info.full_name}</p>
                      </div>
                    )}
                    {selectedApp.basic_info.trn && (
                      <div>
                        <label className="text-xs text-slate-400">TRN</label>
                        <p className="font-medium">{selectedApp.basic_info.trn}</p>
                      </div>
                    )}
                    {selectedApp.basic_info.ppv_badge_number && (
                      <div>
                        <label className="text-xs text-slate-400">PPV Badge Number</label>
                        <p className="font-medium">{selectedApp.basic_info.ppv_badge_number}</p>
                      </div>
                    )}
                    {selectedApp.basic_info.parish && (
                      <div>
                        <label className="text-xs text-slate-400">Parish</label>
                        <p className="font-medium">{selectedApp.basic_info.parish}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedApp.type === 'ppv' && selectedApp.vehicle_info && Object.keys(selectedApp.vehicle_info).length > 0 && (
                <div className="border-t border-slate-700 pt-4 mt-4">
                  <h4 className="text-emerald-400 font-semibold mb-3">Vehicle Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedApp.vehicle_info.vehicle_type && (
                      <div>
                        <label className="text-xs text-slate-400">Vehicle Type</label>
                        <p className="font-medium">{selectedApp.vehicle_info.vehicle_type}</p>
                      </div>
                    )}
                    {selectedApp.vehicle_info.vehicle_make && (
                      <div>
                        <label className="text-xs text-slate-400">Make</label>
                        <p className="font-medium">{selectedApp.vehicle_info.vehicle_make}</p>
                      </div>
                    )}
                    {selectedApp.vehicle_info.vehicle_model && (
                      <div>
                        <label className="text-xs text-slate-400">Model</label>
                        <p className="font-medium">{selectedApp.vehicle_info.vehicle_model}</p>
                      </div>
                    )}
                    {selectedApp.vehicle_info.license_plate && (
                      <div>
                        <label className="text-xs text-slate-400">License Plate</label>
                        <p className="font-medium">{selectedApp.vehicle_info.license_plate}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Documents Section with Download */}
              {selectedApp.documents && (
                <div className="border-t border-slate-700 pt-4 mt-4">
                  <h4 className="text-emerald-400 font-semibold mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Supporting Documents
                  </h4>
                  <div className="grid grid-cols-1 gap-3">
                    {typeof selectedApp.documents === 'object' && !Array.isArray(selectedApp.documents) ? (
                      Object.entries(selectedApp.documents).map(([key, value]) => {
                        if (!value) return null;
                        const rawUrl = typeof value === 'string' ? value : value?.url; const docUrl = rawUrl?.startsWith('/') ? rawUrl : rawUrl;
                        const docName = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                        return (
                          <div key={key} className="flex items-center justify-between bg-slate-800 p-3 rounded-lg">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-blue-400" />
                              <span className="text-sm">{docName}</span>
                            </div>
                            {docUrl && (
                              <div className="flex gap-2">
                                <a
                                  href={docUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs flex items-center gap-1"
                                >
                                  <Eye className="w-3 h-3" /> View
                                </a>
                                <a
                                  href={docUrl}
                                  download
                                  className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 rounded text-xs flex items-center gap-1"
                                >
                                  <Download className="w-3 h-3" /> Download,
  Trash2
                                </a>
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : Array.isArray(selectedApp.documents) && selectedApp.documents.length > 0 ? (
                      selectedApp.documents.map((doc, i) => {
                        const docUrl = typeof doc === 'string' ? doc : doc?.url;
                        const docName = doc?.name || `Document ${i + 1}`;
                        return (
                          <div key={i} className="flex items-center justify-between bg-slate-800 p-3 rounded-lg">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-blue-400" />
                              <span className="text-sm">{docName}</span>
                            </div>
                            {docUrl && (
                              <div className="flex gap-2">
                                <a
                                  href={docUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs flex items-center gap-1"
                                >
                                  <Eye className="w-3 h-3" /> View
                                </a>
                                <a
                                  href={docUrl}
                                  download
                                  className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 rounded text-xs flex items-center gap-1"
                                >
                                  <Download className="w-3 h-3" /> Download,
  Trash2
                                </a>
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-slate-500 text-sm italic">No documents uploaded</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Nearby PUDO Locations Section */}
              {selectedApp.type === "pudo" && (
                <div className="border-t border-slate-700 pt-4 mt-4">
                  <h4 className="text-emerald-400 font-semibold mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Nearby PUDO Locations (within 5km)
                  </h4>
                  
                  {selectedApp.latitude && selectedApp.longitude ? (
                    <>
                      <div className="bg-slate-800/50 p-3 rounded-lg mb-3">
                        <p className="text-xs text-slate-400 mb-1">Applicant Coordinates</p>
                        <p className="text-sm font-mono">
                          {selectedApp.latitude}, {selectedApp.longitude}
                        </p>
                      </div>
                      
                      {loadingNearby ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
                          <span className="ml-2 text-sm text-slate-400">Checking nearby locations...</span>
                        </div>
                      ) : nearbyPudos.length > 0 ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertCircle className="w-4 h-4 text-amber-400" />
                            <span className="text-amber-400 text-sm font-medium">
                              {nearbyPudos.length} PUDO location(s) nearby
                            </span>
                          </div>
                          {nearbyPudos.map((pudo, idx) => (
                            <div key={idx} className="bg-slate-800 p-3 rounded-lg border border-amber-500/30">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-white">{pudo.name}</p>
                                  <p className="text-sm text-slate-400">{pudo.address}, {pudo.city}</p>
                                </div>
                                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                                  {pudo.distance_km} km away
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-green-400 bg-green-500/10 p-3 rounded-lg">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm">No existing PUDO locations within 5km - Good coverage area!</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-slate-500 text-sm italic">No coordinates provided by applicant</p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Add Modal */}
      <Dialog open={showQuickAddModal} onOpenChange={setShowQuickAddModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {quickAddType === 'driver' && <Truck className="w-5 h-5 text-emerald-500" />}
              {quickAddType === 'seller' && <Store className="w-5 h-5 text-blue-500" />}
              {quickAddType === 'pudo' && <MapPin className="w-5 h-5 text-purple-500" />}
              Quick Add {quickAddType.charAt(0).toUpperCase() + quickAddType.slice(1)}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Full Name *</Label>
                <Input
                  value={quickAddForm.name}
                  onChange={(e) => setQuickAddForm({...quickAddForm, name: e.target.value})}
                  placeholder="John Doe"
                  className="bg-slate-800 border-slate-700 mt-1"
                  data-testid="quick-add-name"
                />
              </div>
              <div>
                <Label className="text-slate-300">Email *</Label>
                <Input
                  type="email"
                  value={quickAddForm.email}
                  onChange={(e) => setQuickAddForm({...quickAddForm, email: e.target.value})}
                  placeholder="email@example.com"
                  className="bg-slate-800 border-slate-700 mt-1"
                  data-testid="quick-add-email"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-slate-300">Phone</Label>
              <Input
                value={quickAddForm.phone}
                onChange={(e) => setQuickAddForm({...quickAddForm, phone: e.target.value})}
                placeholder="+1 (876) 000-0000"
                className="bg-slate-800 border-slate-700 mt-1"
                data-testid="quick-add-phone"
              />
            </div>
            
            {quickAddType === 'driver' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Vehicle Type</Label>
                    <Select 
                      value={quickAddForm.vehicle_type} 
                      onValueChange={(val) => setQuickAddForm({...quickAddForm, vehicle_type: val})}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="bicycle">Bicycle</SelectItem>
                        <SelectItem value="motorcycle">Motorcycle</SelectItem>
                        <SelectItem value="car">Car</SelectItem>
                        <SelectItem value="van">Van</SelectItem>
                        <SelectItem value="truck">Truck</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-300">License Plate</Label>
                    <Input
                      value={quickAddForm.license_plate}
                      onChange={(e) => setQuickAddForm({...quickAddForm, license_plate: e.target.value.toUpperCase()})}
                      placeholder="ABC 1234"
                      className="bg-slate-800 border-slate-700 mt-1"
                      data-testid="quick-add-license"
                    />
                  </div>
                </div>
              </>
            )}
            
            {(quickAddType === 'seller' || quickAddType === 'pudo') && (
              <>
                <div>
                  <Label className="text-slate-300">Business Name</Label>
                  <Input
                    value={quickAddForm.business_name}
                    onChange={(e) => setQuickAddForm({...quickAddForm, business_name: e.target.value})}
                    placeholder="Business Name"
                    className="bg-slate-800 border-slate-700 mt-1"
                    data-testid="quick-add-business-name"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">TRN (optional)</Label>
                  <Input
                    value={quickAddForm.trn}
                    onChange={(e) => setQuickAddForm({...quickAddForm, trn: e.target.value})}
                    placeholder="Tax Registration Number"
                    className="bg-slate-800 border-slate-700 mt-1"
                  />
                </div>
              </>
            )}
            
            {quickAddType === 'pudo' && (
              <>
                <div>
                  <Label className="text-slate-300">PUDO Address *</Label>
                  <Input
                    value={quickAddForm.pudo_address}
                    onChange={(e) => setQuickAddForm({...quickAddForm, pudo_address: e.target.value})}
                    placeholder="123 Main Street"
                    className="bg-slate-800 border-slate-700 mt-1"
                    data-testid="quick-add-pudo-address"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">City</Label>
                    <Input
                      value={quickAddForm.pudo_city}
                      onChange={(e) => setQuickAddForm({...quickAddForm, pudo_city: e.target.value})}
                      placeholder="Kingston"
                      className="bg-slate-800 border-slate-700 mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Parish</Label>
                    <Select 
                      value={quickAddForm.pudo_parish} 
                      onValueChange={(val) => setQuickAddForm({...quickAddForm, pudo_parish: val})}
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="Kingston">Kingston</SelectItem>
                        <SelectItem value="St. Andrew">St. Andrew</SelectItem>
                        <SelectItem value="St. Catherine">St. Catherine</SelectItem>
                        <SelectItem value="St. James">St. James</SelectItem>
                        <SelectItem value="Manchester">Manchester</SelectItem>
                        <SelectItem value="Clarendon">Clarendon</SelectItem>
                        <SelectItem value="St. Ann">St. Ann</SelectItem>
                        <SelectItem value="Westmoreland">Westmoreland</SelectItem>
                        <SelectItem value="Portland">Portland</SelectItem>
                        <SelectItem value="St. Thomas">St. Thomas</SelectItem>
                        <SelectItem value="St. Mary">St. Mary</SelectItem>
                        <SelectItem value="Trelawny">Trelawny</SelectItem>
                        <SelectItem value="Hanover">Hanover</SelectItem>
                        <SelectItem value="St. Elizabeth">St. Elizabeth</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Operating Hours</Label>
                  <Input
                    value={quickAddForm.pudo_hours}
                    onChange={(e) => setQuickAddForm({...quickAddForm, pudo_hours: e.target.value})}
                    placeholder="Mon-Sat 9:00 AM - 6:00 PM"
                    className="bg-slate-800 border-slate-700 mt-1"
                  />
                </div>
              </>
            )}
            
            <div className="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                id="sendCredentials"
                checked={quickAddForm.send_credentials_email}
                onChange={(e) => setQuickAddForm({...quickAddForm, send_credentials_email: e.target.checked})}
                className="rounded border-slate-600"
              />
              <label htmlFor="sendCredentials" className="text-sm text-slate-300">
                Send login credentials via email
              </label>
            </div>
            
            <div className="p-3 bg-[#00A86B]/10 border border-[#00A86B]/30 rounded-lg">
              <p className="text-sm text-[#00A86B]">
                <CheckCircle className="w-4 h-4 inline mr-1" />
                This partner will be instantly approved and can start using the platform immediately.
              </p>
            </div>
          </div>
          
          <DialogFooter className="mt-6">
            <Button variant="ghost" onClick={() => setShowQuickAddModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleQuickAdd}
              disabled={quickAddLoading}
              className={
                quickAddType === 'driver' ? 'bg-emerald-600 hover:bg-emerald-700' :
                quickAddType === 'seller' ? 'bg-blue-600 hover:bg-blue-700' :
                'bg-purple-600 hover:bg-purple-700'
              }
              data-testid="quick-add-submit"
            >
              {quickAddLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Add {quickAddType.charAt(0).toUpperCase() + quickAddType.slice(1)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionType === 'approve' ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
              {actionType === 'approve' ? 'Approve Application' : 'Reject Application'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {actionType === 'reject' && (
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Rejection Reason *</label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejection..."
                  className="bg-slate-800 border-slate-700"
                  rows={3}
                />
              </div>
            )}
            
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Admin Notes (optional)</label>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Any additional notes..."
                className="bg-slate-800 border-slate-700"
                rows={2}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sendEmail"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="rounded border-slate-600"
              />
              <label htmlFor="sendEmail" className="text-sm text-slate-300">
                Send email notification to applicant
              </label>
            </div>
          </div>
          
          <DialogFooter className="mt-6">
            <Button variant="ghost" onClick={() => setShowActionDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitAction}
              disabled={processing}
              className={actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : actionType === 'approve' ? (
                <CheckCircle className="w-4 h-4 mr-2" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              {actionType === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
